import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HiArrowLeft,
  HiArrowRight,
  HiAcademicCap,
  HiBriefcase,
  HiCheckBadge,
} from "react-icons/hi2";

import courses from "../data/courses";

const CourseDetails = () => {
  const { slug } = useParams();

  const course = courses.find((item) => item.slug === slug);

  // Course not found
  if (!course) {
    return (
      <section className="min-h-[70vh] flex items-center justify-center px-6 py-20">
        <div className="max-w-xl text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300">
            <HiAcademicCap className="text-3xl" />
          </div>

          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Course Not Found
          </h1>

          <p className="mt-4 text-gray-600 dark:text-gray-300">
            We couldn't find the course you're looking for.
          </p>

          <Link
            to="/courses"
            className="
              mt-8
              inline-flex
              items-center
              gap-2
              rounded-xl
              bg-gradient-to-r
              from-blue-600
              to-indigo-600
              px-6
              py-3
              text-sm
              font-semibold
              text-white
              shadow-md
              transition-all
              duration-300
              hover:-translate-y-0.5
              hover:shadow-lg
              focus:outline-none
              focus:ring-2
              focus:ring-blue-500
              focus:ring-offset-2
              dark:focus:ring-offset-gray-900
            "
          >
            <HiArrowLeft aria-hidden="true" />
            View All Courses
          </Link>
        </div>
      </section>
    );
  }

  return (
    <main className="relative overflow-hidden">
      {/* Background */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          inset-0
          bg-gradient-to-b
          from-blue-50
          via-white
          to-gray-50
          dark:from-gray-950
          dark:via-gray-900
          dark:to-gray-950
        "
      />

      {/* Hero */}
      <section className="relative px-4 pb-16 pt-28 sm:px-6 md:pb-20 md:pt-36">
        <div className="mx-auto max-w-7xl">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="mb-8 text-sm text-gray-500 dark:text-gray-400"
          >
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link
                  to="/"
                  className="transition-colors hover:text-blue-600 dark:hover:text-blue-400"
                >
                  Home
                </Link>
              </li>

              <li aria-hidden="true">/</li>

              <li>
                <Link
                  to="/courses"
                  className="transition-colors hover:text-blue-600 dark:hover:text-blue-400"
                >
                  Courses
                </Link>
              </li>

              <li aria-hidden="true">/</li>

              <li
                className="font-medium text-gray-800 dark:text-gray-200"
                aria-current="page"
              >
                {course.title}
              </li>
            </ol>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]"
          >
            {/* Left */}
            <div>
              {/* Category */}
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 dark:border-blue-900/70 dark:bg-blue-950/40 dark:text-blue-300">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                {course.category}
              </div>

              <h1 className="max-w-4xl text-3xl font-extrabold tracking-tight text-gray-950 dark:text-white sm:text-4xl md:text-5xl lg:text-6xl">
                {course.title}
              </h1>

              <p className="mt-6 max-w-3xl text-base leading-8 text-gray-600 dark:text-gray-300 sm:text-lg">
                {course.description}
              </p>

              {/* Metadata */}
              <div className="mt-8 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                  <HiAcademicCap className="text-blue-600 dark:text-blue-400" />
                  {course.level}
                </div>

                {course.rating !== null && (
                  <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    <span className="text-yellow-500">★</span>
                    {course.rating.toFixed(1)} rating
                  </div>
                )}

                {course.students !== null && (
                  <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    <HiBriefcase className="text-indigo-600 dark:text-indigo-400" />
                    {course.students.toLocaleString()}+ learners
                  </div>
                )}
              </div>

              {/* CTA */}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/register"
                  state={{ selectedCourse: course.title }}
                  className="
                    group
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    bg-gradient-to-r
                    from-blue-600
                    to-indigo-600
                    px-6
                    py-3.5
                    text-sm
                    font-bold
                    text-white
                    shadow-lg
                    shadow-blue-600/20
                    transition-all
                    duration-300
                    hover:-translate-y-0.5
                    hover:shadow-xl
                    hover:shadow-blue-600/25
                    focus:outline-none
                    focus:ring-2
                    focus:ring-blue-500
                    focus:ring-offset-2
                    dark:focus:ring-offset-gray-900
                  "
                >
                  Enquire About This Course
                  <HiArrowRight
                    aria-hidden="true"
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>

                <Link
                  to="/courses"
                  className="
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    border
                    border-gray-300
                    bg-white
                    px-6
                    py-3.5
                    text-sm
                    font-semibold
                    text-gray-700
                    transition-all
                    duration-300
                    hover:-translate-y-0.5
                    hover:border-gray-400
                    hover:shadow-md
                    dark:border-gray-700
                    dark:bg-gray-800
                    dark:text-gray-200
                    dark:hover:border-gray-600
                  "
                >
                  <HiArrowLeft aria-hidden="true" />
                  All Courses
                </Link>
              </div>
            </div>

            {/* Right Course Visual */}
            <div className="flex justify-center lg:justify-end">
              <div
                className="
                  relative
                  flex
                  min-h-[280px]
                  w-full
                  max-w-md
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-3xl
                  border
                  border-gray-200/80
                  bg-white/80
                  p-10
                  shadow-2xl
                  shadow-gray-900/5
                  backdrop-blur-xl
                  dark:border-gray-700
                  dark:bg-gray-800/80
                "
              >
                <div
                  aria-hidden="true"
                  className="
                    absolute
                    -right-20
                    -top-20
                    h-48
                    w-48
                    rounded-full
                    bg-blue-500/10
                    blur-3xl
                  "
                />

                <div
                  aria-hidden="true"
                  className="
                    absolute
                    -bottom-20
                    -left-20
                    h-48
                    w-48
                    rounded-full
                    bg-indigo-500/10
                    blur-3xl
                  "
                />

                <div className="relative flex flex-col items-center text-center">
                  <div
                    className="
                      flex
                      h-24
                      w-24
                      items-center
                      justify-center
                      rounded-3xl
                      bg-gradient-to-br
                      from-blue-600
                      to-indigo-600
                      text-white
                      shadow-xl
                      shadow-blue-600/20
                    "
                  >
                    <div className="text-5xl" aria-hidden="true">
                      {course.icon}
                    </div>
                  </div>

                  <p className="mt-6 text-sm font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    {course.category}
                  </p>

                  <h2 className="mt-2 max-w-xs text-xl font-bold text-gray-900 dark:text-white">
                    {course.title}
                  </h2>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You Get */}
      <section className="relative border-t border-gray-200/80 bg-white/70 px-4 py-14 dark:border-gray-800 dark:bg-gray-900/70 sm:px-6 md:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">
              Learning Experience
            </p>

            <h2 className="mt-3 text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
              Build Practical, Career-Focused Skills
            </h2>

            <p className="mt-4 text-gray-600 dark:text-gray-300">
              Explore a structured learning experience designed around practical
              knowledge and professional development.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-800">
              <HiAcademicCap className="text-2xl text-blue-600 dark:text-blue-400" />

              <h3 className="mt-4 font-bold text-gray-900 dark:text-white">
                Structured Learning
              </h3>

              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Follow a clear learning path from fundamentals to practical
                concepts.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-800">
              <HiCheckBadge className="text-2xl text-emerald-600 dark:text-emerald-400" />

              <h3 className="mt-4 font-bold text-gray-900 dark:text-white">
                Practical Experience
              </h3>

              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Strengthen your skills through practical learning and
                application-focused training.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-800">
              <HiBriefcase className="text-2xl text-indigo-600 dark:text-indigo-400" />

              <h3 className="mt-4 font-bold text-gray-900 dark:text-white">
                Career Preparation
              </h3>

              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Develop professional skills that can support your career
                preparation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative px-4 py-14 sm:px-6 md:py-20">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 px-6 py-12 text-center text-white shadow-2xl sm:px-10 md:px-16">
          <h2 className="text-2xl font-bold sm:text-3xl">
            Ready to explore {course.title}?
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-blue-100 sm:text-base">
            Connect with IA4Solutions to learn more about the course,
            curriculum, training options, and registration process.
          </p>

          <Link
            to="/register"
            state={{ selectedCourse: course.title }}
            className="
              mt-7
              inline-flex
              items-center
              gap-2
              rounded-xl
              bg-white
              px-6
              py-3
              text-sm
              font-bold
              text-blue-700
              shadow-lg
              transition-all
              duration-300
              hover:-translate-y-0.5
              hover:shadow-xl
            "
          >
            Enquire Now
            <HiArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default CourseDetails;